/**
 * The figures a jewellery bill can carry.
 *
 * No two shops quote the same way. One prices metal and folds everything into
 * making; the next quotes wastage separately and argues about it; a third sells
 * stones by weight and lists them apart from the gold. Building one fixed bill
 * means most shops see columns that mean nothing to them and miss the one they
 * live by.
 *
 * So every figure a jewellery bill can carry is here, and each shop turns on
 * the ones it uses. The engine works all of them out regardless — turning a
 * column off hides it, it does not change what the piece costs.
 */
export interface DocumentColumn {
  key: string;
  label: string;
  /** What it means in a jeweller's words, shown when choosing columns. */
  about: string;
  /** Always present: the bill makes no sense without it. */
  fixed?: boolean;
  /** The cashier can type into this one, not only read it. */
  editable?: boolean;
}

export const SALE_COLUMNS: DocumentColumn[] = [
  { key: 'item', label: 'Item', about: 'The piece and its tag number.', fixed: true },
  { key: 'purity', label: 'Purity', about: '916, 22K, 999 — what the piece is.' },
  { key: 'grossWt', label: 'Gross wt', about: 'What the piece weighs on the scale, stones and all.' },
  { key: 'stoneWt', label: 'Stone wt', about: 'The weight of the stones, taken off the gross.' },
  { key: 'netWt', label: 'Net wt', about: 'Gross less stones — the metal you are selling.' },
  { key: 'fineWt', label: 'Fine wt', about: 'Net weight expressed as pure metal, at this purity.' },
  { key: 'rate', label: 'Rate/g', about: "Today's rate for this purity. Editable at the counter.", editable: true },
  { key: 'metal', label: 'Metal value', about: 'Fine weight times the rate.' },
  { key: 'wastage', label: 'Wastage', about: 'The percentage or grams added for loss in making.', editable: true },
  { key: 'making', label: 'Making', about: 'Labour: per gram, per piece, or a percentage.', editable: true },
  {
    key: 'stoneAmount',
    label: 'Stone value',
    about: 'What the stones are charged at, separately from the gold. Editable at the counter.',
    editable: true,
  },
  { key: 'otherCharges', label: 'Other charges', about: 'Hallmarking, polish, certification.' },
  { key: 'discount', label: 'Discount', about: 'Given off this line.', editable: true },
  { key: 'tax', label: 'GST', about: 'The tax on this line.' },
  { key: 'cost', label: 'Cost', about: 'What the piece cost the shop. Only for those allowed to see profit.' },
  { key: 'profit', label: 'Profit', about: 'Line total less cost. Only for those allowed to see profit.' },
  { key: 'total', label: 'Line total', about: 'What the customer pays for this piece.', fixed: true },
];

export const PURCHASE_COLUMNS: DocumentColumn[] = [
  { key: 'photo', label: 'Photo', about: 'A picture of the piece, taken as it is unpacked.' },
  { key: 'product', label: 'Product', about: 'Which design this piece is.', fixed: true },
  { key: 'tag', label: 'Tag no.', about: 'The number written on the tag. Generated if left blank.' },
  { key: 'barcode', label: 'Barcode', about: 'For shops that print barcodes.' },
  { key: 'purity', label: 'Purity', about: 'What the supplier has supplied.', fixed: true },
  { key: 'grossWt', label: 'Gross wt', about: 'Weighed as it arrives.', fixed: true },
  { key: 'stoneWt', label: 'Stone wt', about: 'Stones in the piece, taken off the gross.' },
  { key: 'netWt', label: 'Net wt', about: 'Gross less stones, shown as you type.' },
  { key: 'rate', label: 'Rate/g', about: "The supplier's rate per gram. The cost is worked out from it." },
  { key: 'wastage', label: 'Wastage', about: 'What the supplier charged above the metal, % or ₹.' },
  { key: 'making', label: 'Making', about: 'Labour the supplier charged: per gram, flat or %.' },
  { key: 'stoneAmount', label: 'Stones ₹', about: 'What the stones cost, on top of the metal.' },
  { key: 'cost', label: 'Cost', about: 'What this piece cost, all in. Profit is measured against it.', fixed: true },
  { key: 'place', label: 'Place', about: 'Which showcase or safe it goes into.' },
];

/** What a shop that has never chosen sees when buying. */
export const DEFAULT_PURCHASE_COLUMNS = 'photo,tag,purity,grossWt,stoneWt,rate,wastage,making,stoneAmount,cost,place';

/** The columns a shop has turned on, in the order the bill works them out. */
export function chosenColumns(setting: string, all: DocumentColumn[]): DocumentColumn[] {
  const wanted = new Set(
    setting
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean),
  );
  return all.filter((c) => c.fixed || wanted.has(c.key));
}
